import React, { useContext, useEffect, useRef } from 'react';
import { MapContainer, ZoomControl } from 'react-leaflet';
import { FloorSelectorControl } from './FloorSelectorControl';
import LayersController from './LayersController';
import { TileLayer } from 'react-leaflet';
import config from '../config';
import { MapContainerRefContext } from '../utils/mapContainerRef.context';
import useDevice from 'hooks/useDevice';
import L from 'leaflet';
import { observer } from 'mobx-react';
import { useMapStore } from 'contexts/RootStore.context';
import LoadingScreen from 'components/Loading/LoadingScreen';
import KeyLegend from './KeyLegend';
import { StyledMapWrapper } from '../Styled';

import 'leaflet/dist/leaflet.css';
import 'assets/scss/leaflet-custom.scss';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import LoadingDots from 'components/Loading/LoadingDots';
import { style } from 'typestyle';
import { Horizontal } from 'gls';
import { colours } from 'styling/colours';

export const MapContainerWrapper = observer(() => {

  const {
    loading,
    loadingAssets,
    loadingUsers,
    selectedBuildingId,
    levels,
    setMapZoom,
    handleMapMove
  } = useMapStore()
  const { map, setMap } = useContext(MapContainerRefContext);
  const { isMobileDevice } = useDevice();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const bounds = levels ? L.geoJSON(levels).getBounds() : undefined; // set bounds of map to levelFeature bounds.


  useEffect(() => {

    const handleZoom = () => setMapZoom(map?.getZoom() || 14)
    const updateVisibleCounts = () => {
      if (!map) return
      const bounds = map.getBounds();
      handleMapMove(bounds)
    };

    if (map) {
      map.on('zoomend', handleZoom);
      map.on('moveend zoomend', updateVisibleCounts);
    }

    return () => {
      map?.off('zoomend', handleZoom);
      map?.off('moveend zoomend', updateVisibleCounts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map])

  // Leaflet only measures its container once on init. 
  // Watch the wrapper and re-sync leaflet when it changes to fix partially grey issue.
  useEffect(() => {
    if (!map || !wrapperRef.current)
      return

    const resizeObserver = new ResizeObserver(() => map.invalidateSize());
    resizeObserver.observe(wrapperRef.current);

    return () => resizeObserver.disconnect();
  }, [map])

  if (loading || !levels)
    return <LoadingScreen />

  return (
    <StyledMapWrapper ref={wrapperRef}>
      <LoadingData loadingAssets={loadingAssets} loadingUsers={loadingUsers} />
      <MapContainer
        bounds={bounds}
        maxZoom={config.maxZoom}
        minZoom={config.minZoom}
        zoom={config.zoom}
        scrollWheelZoom={false}
        zoomControl={false}
        ref={setMap} // set map ref to usestate in parent to be passed to external components.
      >
        {selectedBuildingId !== "" && <FloorSelectorControl />}
        {!isMobileDevice && <ZoomControl position="topright" />}
        <LayersController />
        <KeyLegend />
        <TileLayer
          updateWhenZooming
          opacity={0.5}
          zIndex={-1}
          maxNativeZoom={19} // upscale OSM tiles, because they dont give lvl 20.
          maxZoom={config.maxZoom}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
      </MapContainer>
    </StyledMapWrapper>
  );
})

const dataLoadingStyle = style({
  position: "absolute",
  margin: '10px',
  width: '300px',
  height: '20px',
  top: 0,
  left: 0,
  zIndex: 9999,
  $nest: {
    "div": {
      margin: '0'
    },
    "span": {
      color: colours.secondaryText
    }
  }
})

type loadingDataProps = {
  loadingAssets: boolean
  loadingUsers: boolean
}
const LoadingData = ({ loadingAssets, loadingUsers }: loadingDataProps) => {

  if (loadingAssets || loadingUsers) {
    <Horizontal className={dataLoadingStyle} spacing={5} horizontalAlign='left'>
      <LoadingDots />
      <span>
        {loadingUsers && loadingAssets && 'loading Assets and Users'}
        {loadingUsers && !loadingAssets && 'loading Users'}
        {!loadingUsers && loadingAssets && 'loading Assets'}
      </span>
    </Horizontal>
  }
  return null
}